// eslint-disable-next-line no-unused-vars
import React, { useRef } from 'react';
import { array, bool, func } from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Package } from 'lucide-react';

import Img from 'Containers/Image/Image';
import PromptModal from 'Containers/PromptModal/PromptModal';
import { OFFER_PRODUCT_STATUS_TRANSLATIONS } from 'Utils/offers';

const StyledContent = styled.div`
  color: ${ props => props.theme.colors.darkGray };

  > p {
    margin-bottom: 1rem;
  }

  .products {
    max-height: 360px;
    overflow-y: auto;
  }

  .product {
    border-top: 1px solid ${ props => props.theme.products.colors.border };
    display: grid;
    gap: 0.75rem;
    grid-template-columns: 56px minmax(0, 1fr) auto;
    padding: 0.875rem 0;
  }

  .preview {
    align-items: center;
    border: 1px solid ${ props => props.theme.products.colors.border };
    display: flex;
    height: 56px;
    justify-content: center;
    overflow: hidden;
    padding: 3px;
    width: 56px;

    .image {
      max-height: 100%;
      max-width: 100%;
    }
  }

  .title {
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.2rem;
    overflow: hidden;
  }

  .status-message,
  .stock {
    font-size: 0.75rem;
  }

  .sku {
    align-items: center;
    color: ${ props => props.theme.colors.darkGray };
    display: flex;
    font-size: 0.875rem;
    margin: 5px 0;

    .sku-label {
      font-weight: 600;
    }

    .sku-value {
      font-weight: normal;
      margin-left: 5px;
    }
  }

  .status-message {
    color: ${ props => props.theme.colors.red };
    margin-top: 0.35rem;
  }

  .stock {
    margin-right: 1rem;
    min-width: 72px;
    text-align: center;

    strong {
      color: ${ props => props.theme.colors.red };
      display: block;
      font-size: 0.875rem;
    }
  }
`;

const UnavailableProductsConfirmationModal = ({
  open,
  products,
  onConfirm,
  onCancel
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const displayedProductsRef = useRef([]);
  if (products.length) {
    displayedProductsRef.current = products;
  }
  const displayedProducts = products.length ? products : displayedProductsRef.current;
  const body = (
    <StyledContent theme={ theme }>
      <p>{ t('offers.cartUnavailableProductsMessage') }</p>
      <div className="products">
        { displayedProducts.map(product => (
          <article className="product" key={ product.productId || product.id }>
            <div className="preview">
              <Img
                className="image"
                src={ product.pictureUrl }
                unloader={ <Package size={ 28 } strokeWidth={ 1.25 } /> }
                alt={ product.title || product.name || '' }
              />
            </div>
            <div>
              <p className="title">{ product.title || product.name }</p>
              <p className="sku">
                <span className="sku-label">{ t('products.code') }:</span>
                <span className="sku-value">{ product.code || product.erpProductId || '-' }</span>
              </p>
              <p className="status-message">
                { t(
                  OFFER_PRODUCT_STATUS_TRANSLATIONS[product.status]
                  || 'offers.cartStatusProductUnavailable'
                ) }
              </p>
            </div>
            <p className="stock">
              { t('offers.stock') }
              <strong>{ product.stock ?? product.stockValue ?? 0 }</strong>
            </p>
          </article>
        )) }
      </div>
    </StyledContent>
  );

  return (
    <PromptModal
      modalOpen={ open }
      className="UnavailableProductsConfirmationModal"
      size="md"
      onCloseModal={ () => {} }
      onCancel={ onCancel }
      onConfirm={ onConfirm }
      header={ t('offers.cartConfirmationTitle') }
      body={ body }
      confirmButton={ t('common.agree') }
      cancelButton={ t('common.cancel') }
    />
  );
};

UnavailableProductsConfirmationModal.propTypes = {
  open: bool,
  products: array,
  onConfirm: func,
  onCancel: func
};

UnavailableProductsConfirmationModal.displayName = 'UnavailableProductsConfirmationModal';

export default UnavailableProductsConfirmationModal;
